import Axios, { AxiosResponse } from 'axios';
import { useAtom } from 'jotai';
import React from 'react';
import { environment } from '../../environment';
import { useLogin } from '../login';
import {
    brandNameCacheAtom,
    IBrandSearchPage,
    IBrandSearchQuery,
    IBrandOption
} from './types';

/** Debounce applied to keystrokes before a search request goes out. */
const SEARCH_DEBOUNCE_MS = 250;
/** Rows per page. Matches the API default. */
const PAGE_SIZE = 25;

// #region useBrandSearch
/**
 * Server-side brand search for a dropdown.
 *
 * The brand table is larger than any single fetch can return, so filtering MUST
 * happen in the database — the previous approach fetched one page of brands into
 * an atom and filtered it client-side, which made every brand outside that page
 * (and every inactive/unnamed brand) permanently unfindable.
 *
 * Responses are matched against a request sequence number, so a slow response
 * for an older query can never overwrite the results of a newer one.
 */
export const useBrandSearch = (options?: IBrandSearchQuery & { enabled?: boolean }) => {
    const [, authToken, loginFunctions] = useLogin();

    const [query, setQuery] = React.useState('');
    const [debouncedQuery, setDebouncedQuery] = React.useState('');
    const [brands, setBrands] = React.useState<IBrandOption[]>([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [offset, setOffset] = React.useState(0);

    const enabled = options?.enabled !== false;
    const limit = options?.limit ?? PAGE_SIZE;

    // Query params are read through a ref so changing them (or the token) does not
    // retrigger the fetch effect on every parent render.
    const paramsRef = React.useRef({ authToken, limit, active: options?.active, includeUnnamed: options?.includeUnnamed, full: options?.full, hasOrders: options?.hasOrders });
    paramsRef.current = { authToken, limit, active: options?.active, includeUnnamed: options?.includeUnnamed, full: options?.full, hasOrders: options?.hasOrders };

    // Monotonic request id. Only the newest request may write state.
    const requestSeq = React.useRef(0);

    // Debounce the term, and reset paging whenever it changes — page 2 of the old
    // term is meaningless for the new one.
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query.trim());
            setOffset(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    React.useEffect(() => {
        if (!enabled || !paramsRef.current.authToken) return;

        const seq = ++requestSeq.current;
        const controller = new AbortController();
        const isFirstPage = offset === 0;

        const run = async () => {
            setLoading(true);
            const { authToken: token, limit: pageLimit, active, includeUnnamed, full, hasOrders } = paramsRef.current;
            try {
                const response: AxiosResponse<IBrandSearchPage> = await Axios.get(`${environment.brandsUrl}/search`, {
                    headers: { 'X-Wallmates-Auth': token },
                    signal: controller.signal,
                    params: {
                        q: debouncedQuery || undefined,
                        active,
                        includeUnnamed: includeUnnamed ? 'true' : undefined,
                        full: full ? 'true' : undefined,
                        hasOrders: hasOrders ? 'true' : undefined,
                        limit: pageLimit,
                        offset
                    }
                });

                if (seq !== requestSeq.current) return; // a newer query already won
                const page = response.data?.brands ?? [];
                setBrands((prev) => (isFirstPage ? page : dedupeById([...prev, ...page])));
                setTotal(response.data?.total ?? page.length);
            } catch (err: any) {
                if (controller.signal.aborted || seq !== requestSeq.current) return;
                // A failed dropdown search must not log the user out mid-typing;
                // surface it as "no results" and let errorCatcher handle 401s.
                if (err?.response?.data?.status === 401) loginFunctions.logout();
                if (isFirstPage) setBrands([]);
            } finally {
                if (seq === requestSeq.current) setLoading(false);
            }
        };

        run();
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery, offset, enabled, authToken]);

    const loadMore = React.useCallback(() => {
        setOffset((prev) => prev + limit);
    }, [limit]);

    const reset = React.useCallback(() => {
        setQuery('');
        setDebouncedQuery('');
        setOffset(0);
    }, []);

    const hasMore = brands.length < total;

    return { brands, total, loading, query, setQuery, loadMore, hasMore, reset };
};

// #region useBrandNames
/**
 * Resolves brand ids to names through a shared cache, fetching only the ids it
 * has never seen. Selected brands are frequently absent from the current search
 * page, so this — not the option list — is the source of truth for labels.
 */
export const useBrandNames = (ids: Array<number | null | undefined>) => {
    const [, authToken, loginFunctions] = useLogin();
    const [cache, setCache] = useAtom(brandNameCacheAtom);

    // Ids in flight, so several pickers mounting at once don't each fetch the
    // same id before the first response lands.
    const inFlight = React.useRef<Set<number>>(new Set());

    const wanted = React.useMemo(
        () => Array.from(new Set(ids.map(Number).filter((id) => Number.isInteger(id) && id > 0))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(ids)]
    );

    React.useEffect(() => {
        if (!authToken) return;

        const missing = wanted.filter((id) => cache[id] === undefined && !inFlight.current.has(id));
        if (missing.length === 0) return;

        missing.forEach((id) => inFlight.current.add(id));

        const run = async () => {
            try {
                const response: AxiosResponse<Array<{ id: number; name: string }>> = await Axios.post(
                    `${environment.brandsUrl}/names`,
                    { ids: missing },
                    { headers: { 'X-Wallmates-Auth': authToken } }
                );

                const resolved = response.data ?? [];
                setCache((prev) => {
                    const next = { ...prev };
                    for (const entry of resolved) next[Number(entry.id)] = entry.name;
                    // Cache a placeholder for ids the server did not return (deleted
                    // brands), otherwise every render re-requests them forever.
                    for (const id of missing) if (next[id] === undefined) next[id] = '';
                    return next;
                });
            } catch (err: any) {
                if (err?.response?.data?.status === 401) loginFunctions.logout();
            } finally {
                missing.forEach((id) => inFlight.current.delete(id));
            }
        };

        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wanted, authToken]);

    const nameOf = React.useCallback(
        (id: number | null | undefined): string | undefined => {
            if (id === null || id === undefined) return undefined;
            const name = cache[Number(id)];
            return name ? name : undefined;
        },
        [cache]
    );

    return { names: cache, nameOf };
};

// #region searchBrandsRequest
/**
 * Imperative one-shot brand search, for callers that are not a dropdown (e.g. the
 * Brands admin table, which drives its own paging).
 */
export const searchBrandsRequest = async (
    authToken: string,
    params: IBrandSearchQuery,
    signal?: AbortSignal
): Promise<IBrandSearchPage> => {
    const response: AxiosResponse<IBrandSearchPage> = await Axios.get(`${environment.brandsUrl}/search`, {
        headers: { 'X-Wallmates-Auth': authToken },
        signal,
        params: {
            q: params.q || undefined,
            active: params.active,
            includeUnnamed: params.includeUnnamed ? 'true' : undefined,
            full: params.full ? 'true' : undefined,
            hasOrders: params.hasOrders ? 'true' : undefined,
            limit: params.limit ?? PAGE_SIZE,
            offset: params.offset ?? 0
        }
    });

    return response.data ?? { brands: [], total: 0, limit: params.limit ?? PAGE_SIZE, offset: params.offset ?? 0 };
};

// #region dedupeById
/** Paging can overlap when rows shift between requests; keep the first of each id. */
const dedupeById = (rows: IBrandOption[]): IBrandOption[] => {
    const seen = new Set<number>();
    return rows.filter((row) => (seen.has(row.id) ? false : (seen.add(row.id), true)));
};

// #region useBrandIdsByNames
/**
 * Resolves a fixed set of brand NAMES to ids via the search endpoint.
 *
 * Some filters are defined in terms of specific brands (e.g. Orders' "all except
 * Wall Blush & Paperbloom"). Those ids used to be derived by scanning the
 * client-side brand list, which silently produced an incomplete exclusion set
 * whenever a brand fell outside the fetched page.
 */
export const useBrandIdsByNames = (names: string[]) => {
    const [, authToken, loginFunctions] = useLogin();
    const [ids, setIds] = React.useState<number[]>([]);

    const key = names.join('|').toLowerCase();

    React.useEffect(() => {
        if (!authToken) return;
        const controller = new AbortController();
        const normalize = (name: string) => name.trim().toLowerCase().replace(/\s+/g, '');
        const wanted = new Set(names.map(normalize));

        const run = async () => {
            try {
                // One search per name, then an exact (normalized) name match — an
                // ilike search returns substring hits we must not exclude.
                const pages = await Promise.all(
                    names.map((name) => searchBrandsRequest(authToken, { q: name, limit: 100 }, controller.signal))
                );
                const matched = pages
                    .flatMap((page) => page.brands ?? [])
                    .filter((brand) => wanted.has(normalize(brand.name || '')))
                    .map((brand) => brand.id);
                if (!controller.signal.aborted) setIds(Array.from(new Set(matched)));
            } catch (err: any) {
                if (err?.response?.data?.status === 401) loginFunctions.logout();
            }
        };

        run();
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, authToken]);

    return ids;
};
